import React, { useState, useCallback, useEffect, useRef } from "react";
import DOMPurify from 'dompurify';
import {
  Box,
  Slide,
  List,
  Button,
  Chip,
  Alert,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Enviar from "./Enviar";
import ProgramarEnvioModal from "./ProgramarEnvioModal";
import FileCard from "../redactar-mail/FileCard";
import AlertMessage from "../../../../Alias/AlertMessage";
import { useDifusiones } from "../../../context/DifusionesContext";
import { useBody } from "../../../../Body/state/BodyContext";
import { useAuth } from "../../../../Auth/State/AuthContext";

export default function Verificacion({ onClose, images, setImages, newBodyHtml, direccion, form, setForm, bodyHtml, setBodyHtml, aliasLimitado, onBack }) {
  const { crearDifusion, obtenerDifusionesByAreas, areasUser } = useDifusiones();
  const { setSuccess, setMessage } = useBody();
  const { user } = useAuth();
  const isSm = useMediaQuery('(max-width:600px)');
  const [loading, setLoading] = useState(false);
  const [tipoEnvio, setTipoEnvio] = useState(null);
  const [alert, setAlert] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewOverflows, setPreviewOverflows] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (previewRef.current) {
      setPreviewOverflows(previewRef.current.scrollHeight > 200);
    }
  }, [bodyHtml]);

  const resetForm = () => {
    setForm({
      alias: "",
      asunto: "",
      listasSeleccionadas: [],
      listasNoSeleccionadas: [],
      adjuntos: [],
      fechaProgramada: null,
    });
    setImages([]);
    setBodyHtml("");
  };

  const handleSubmit = useCallback(async (fechaProgramada = null) => {
    setTipoEnvio(fechaProgramada ? 'programar' : 'enviar');
    setLoading(true);
    let success = false;
    try {
      const formData = await buildFormData({ form, images, newBodyHtml, user, fechaProgramada });
      await crearDifusion(formData);
      setSuccess(fechaProgramada ? "La difusión ha sido programada" : "La difusión ha sido creada");
      success = true;
    } catch (error) {
      console.error("Error al enviar la difusión:", error);
      setMessage("Hubo un error al intentar crear la difusión");
    } finally {
      setLoading(false);
      setTipoEnvio(null);
    }
    if (success) {
      resetForm();
      obtenerDifusionesByAreas(areasUser);
      onClose();
    }
  }, [form, images, newBodyHtml, user]);

  const buildFormData = async ({
    form,
    images,
    newBodyHtml,
    user,
    fechaProgramada,
  }) => {
    const formData = new FormData();

    form.adjuntos.forEach((file, i) => formData.append(`file${i}`, file));
    images.forEach((image, i) => {
      if (newBodyHtml.includes(image.uid)) {
        formData.append(`image${i}`, image);
        formData.append(`image${i}`, image.name);
      }
    });

    const predeterminadas = form.listasSeleccionadas.filter(l => l.propietario === "Predeterminado");
    const listasID = form.listasSeleccionadas
      .filter(l => l.propietario !== "Predeterminado")
      .map(l => l.id);

    formData.append("alias", form.alias);
    formData.append("alias_nombre", form.aliasNombre || "");
    formData.append("area", form.area);
    formData.append("area_nombre", form.area_nombre);
    formData.append("asunto", form.asunto);
    formData.append("listasID", listasID);
    formData.append("listasPredetermiandas", JSON.stringify(predeterminadas));
    formData.append("bodyHtml", newBodyHtml);
    formData.append("user", user.uid);

    const horaEntrega = fechaProgramada instanceof Date && !isNaN(fechaProgramada)
      ? fechaProgramada.toISOString()
      : null;
    formData.append("horaEntregaProgramada", horaEntrega);
    formData.append("replyTo", form.replyTo || "");

    return formData;
  };

  return (
    <Slide direction={direccion} in mountOnEnter unmountOnExit>
      <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <AlertMessage alert={alert} setAlert={setAlert} />

        {aliasLimitado && (
          <Alert severity="warning">
            El alias <strong>{form.alias}</strong> alcanzó el límite diario de envíos. El correo puede demorar hasta 24hs en enviarse.
          </Alert>
        )}

        <Box>
          <strong>Remitente:</strong>{' '}
          {form.aliasNombre ? `${form.aliasNombre} <${form.alias}>` : form.alias}
        </Box>

        {form.replyTo && (
          <Box><strong>Responder a:</strong> {form.replyTo}</Box>
        )}

        <Box sx={{ overflowWrap: "break-word", overflow: "auto" }}>
          <strong>Asunto:</strong> {form.asunto}
        </Box>

        <Box><strong>Previsualización:</strong></Box>

        <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
          <Box
            ref={previewRef}
            sx={{
              maxHeight: previewExpanded ? "none" : 200,
              overflow: "hidden",
              overflowWrap: "break-word",
              p: 2,
              position: "relative",
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bodyHtml + (form.firma?.firmaHtml ? "<hr/>" + form.firma.firmaHtml : "")),
            }}
          />
          {previewOverflows && (
            <Button
              size="small"
              onClick={() => setPreviewExpanded(prev => !prev)}
              sx={{ width: "100%", borderTop: "1px solid #eee", borderRadius: 0, py: 0.5, textTransform: "none" }}
            >
              {previewExpanded ? "Mostrar Menos ▲" : "Mostrar Más ▼"}
            </Button>
          )}
        </Box>

        <Box><strong>Listas Destinatarias:</strong></Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {form.listasSeleccionadas.map((lista, i) => (
            <Chip key={i} label={lista.nombre} variant="outlined" />
          ))}
        </Box>

        {form.adjuntos.length > 0 &&
          <>
            <Box>
              <strong>Adjuntos:</strong>
            </Box>

            <List>
              {form.adjuntos.map((adjunto, i) => (
                <FileCard key={i} nombreDelArchivo={adjunto.path} type={adjunto.type} />
              ))}
            </List>
          </>
        }

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, my: 3 }}>
          <Button sx={{ textTransform: 'none' }} variant="outlined" onClick={onBack} startIcon={<ArrowBackIcon />} disabled={loading}>
            {!isSm && 'Volver'}
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ProgramarEnvioModal form={form} setForm={setForm} enviar={handleSubmit} loading={loading} tipoEnvio={tipoEnvio} />
            <Enviar fechaDeEnvio={form.fechaProgramada} enviar={handleSubmit} loading={loading} tipoEnvio={tipoEnvio} />
          </Box>
        </Box>
      </Box>
    </Slide>
  );
}
